import { ChangeDetectionStrategy, Component, inject, input, viewChild } from '@angular/core';

import 'chartjs-adapter-moment';
import { Chart, registerables, Interaction, InteractionModeFunction } from 'chart.js';
import { CrosshairPlugin } from '../plugins/chartjs-plugin-crosshair.esm';
import { interpolate } from '../plugins/interpolate';
import { TranslateService } from '@ngx-translate/core';
import { BaseChartDirective } from 'ng2-charts';
import { ChartOptionsButtonComponent } from '../chart-options/chart-options-button.component';
import { ChartOptionsConfig } from '../chart-options/chart-options-storage.service';
import { applyYAxisMode } from '../chart-options/apply-y-axis-mode';
import { ChartLegendComponent } from '../chart-legend/chart-legend.component';
import { HtmlLegendItem, HtmlLegendPlugin } from '../chart-legend/chartjs-plugin-html-legend';

Chart.register(...registerables);

declare module 'chart.js' {
  interface InteractionModeMap {
    interpolate: InteractionModeFunction;
  }
}

Interaction.modes.interpolate = interpolate;

@Component({
  selector: 'cosmos-predictive-chart',
  imports: [BaseChartDirective, ChartOptionsButtonComponent, ChartLegendComponent],
  templateUrl: './predictive-chart.component.html',
  styles: [`
    :host {
      display: block;
      min-width: 0;
    }
    .chart-wrapper {
      position: relative;
      width: 100%;
      min-width: 0;
      height: 450px;
    }
    canvas {
      display: block;
      width: 100%;
      height: 100%;
    }
    .chart-ribbon {
      display: flex;
      justify-content: flex-end;
      margin-bottom: 1rem;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PredictiveChartComponent {
  private readonly translateService = inject(TranslateService);
  private readonly chart = viewChild(BaseChartDirective);

  readonly realData = input<any>(null);
  readonly predictionData = input<any>(null);

  /** Stable id for the per-chart options button/localStorage persistence.
   *  Omit to not render the options button at all. */
  readonly chartId = input<string | undefined>(undefined);

  readonly chartType = 'line';
  // HtmlLegendPlugin no-ops unless chartOptions.plugins.htmlLegend is set.
  readonly chartPlugins = [CrosshairPlugin, HtmlLegendPlugin];
  readonly chartData: { datasets: any[]; labels: any[] };
  chartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        type: 'time',
        ticks: {
          major: { enabled: true },
        },
      },
      y: { type: 'linear', ticks: {} },
    },
    plugins: {
      tooltip: {
        enabled: true,
        mode: 'index',
        intersect: false,
      },
      crosshair: {
        zoom: { enabled: true },
      },
      legend: { display: false },
      htmlLegend: { onLabelsChange: (items: HtmlLegendItem[]) => { this.legendItems = items; } },
    },
  };

  /** Current legend items — HtmlLegendPlugin (afterUpdate) refreshes this
   *  whenever chart data/visibility changes; ChartLegendComponent renders it. */
  legendItems: HtmlLegendItem[] = [];

  /** Current Y-axis display preference from ChartOptionsButtonComponent. No
   *  threshold-bands concept here, so 'auto-bands' is never offered. */
  private _yAxisConfig: ChartOptionsConfig = { yAxisMode: 'auto-data' };

  constructor() {
    const realData = this.realData();
    const predictionData = this.predictionData();

    const realTime = (realData?.time ?? []).map((e: any) => new Date(e).getTime());
    const predictionTime = (predictionData?.time ?? []).map((e: any) => new Date(e).getTime());

    this.chartData = {
      datasets: [
        this.generateDataset(predictionTime, predictionData?.strength ?? [], this.translateService.instant('chart.legend.strength-prediction')),
        this.generateDataset(realTime, realData?.strength ?? [], this.translateService.instant('chart.legend.strength')),
      ],
      labels: [],
    };
  }

  generateDataset(dataX: any[], dataY: any[], label: string) {
    const data = dataX.map((x: any, i: number) => ({ x, y: dataY[i] }));
    return {
      showLine: true,
      fill: false,
      pointRadius: 2,
      label,
      data,
      lineTension: 0,
      interpolate: true,
      cubicInterpolationMode: 'monotone',
    };
  }

  /** ChartOptionsButtonComponent (configChange) handler. See
   *  DynamicLineChartComponent.onChartOptionsChange for why the no-live-chart
   *  branch reassigns chartOptions to a new object instead of mutating in
   *  place — without it, a stored (non-default) config silently gets
   *  dropped on first load, since viewChild(BaseChartDirective) isn't
   *  resolved yet when this first fires from ChartOptionsButtonComponent's
   *  own ngOnInit. */
  onChartOptionsChange(config: ChartOptionsConfig): void {
    this._yAxisConfig = config;

    if (!this.chart()) {
      this.chartOptions = { ...this.chartOptions, scales: { ...this.chartOptions.scales, y: { ...this.chartOptions.scales.y } } };
      applyYAxisMode(this.chartOptions.scales.y, this._yAxisConfig, null);
      return;
    }

    applyYAxisMode(this.chartOptions.scales.y, this._yAxisConfig, null);
    this.chart()?.update();
  }

  /** ChartLegendComponent (toggle) handler — click a legend item to show/hide
   *  that dataset (see MultiLineChartComponent.onLegendToggle). */
  onLegendToggle(datasetIndex: number): void {
    const chart = this.chart();
    if (!chart) return;
    chart.hideDataset(datasetIndex, !chart.isDatasetHidden(datasetIndex));
  }
}
